/**
 * AttachmentDropzone - drag-and-drop + click-to-pick file selector.
 *
 * Enforces the SPEC §8 limits client-side:
 *   - max 15 files per upload
 *   - max 10 MB each
 *
 * The actual upload wire path goes through the comment composer; this
 * component is purely a controlled file picker. The parent renders the
 * file chips and passes the trimmed list back in via `value`.
 *
 * NOTE: server-side upload is wired in Phase 8 along with email +
 * Spaces signing. Until then this component stages the File objects but
 * the composer drops them on submit. A TODO is left below for the
 * Phase 8 hand-off.
 */
import { useRef, useState, type ChangeEvent, type DragEvent } from "react";

const MAX_FILES = 15;
const MAX_BYTES = 10 * 1024 * 1024;

export interface AttachmentDropzoneProps {
  value: File[];
  onChange: (files: File[]) => void;
  /** Render the picker as compact (no help text). Used in the composer row. */
  compact?: boolean;
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export function AttachmentDropzone({
  value,
  onChange,
  compact = false,
}: AttachmentDropzoneProps): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function accept(incoming: File[]) {
    const merged = [...value];
    const errors: string[] = [];
    for (const f of incoming) {
      if (merged.length >= MAX_FILES) {
        errors.push(`Maximum ${MAX_FILES} files per upload.`);
        break;
      }
      if (f.size > MAX_BYTES) {
        errors.push(`${f.name} exceeds the 10 MB per-file limit.`);
        continue;
      }
      merged.push(f);
    }
    setError(errors[0] ?? null);
    onChange(merged);
  }

  function handleInputChange(e: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    if (files.length > 0) accept(files);
    // Reset so re-picking the same file re-fires onChange.
    e.target.value = "";
  }

  function handleDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files ?? []);
    if (files.length > 0) accept(files);
  }

  function removeAt(idx: number) {
    onChange(value.filter((_, i) => i !== idx));
    setError(null);
  }

  return (
    <div className="space-y-2">
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") inputRef.current?.click();
        }}
        className={`flex cursor-pointer items-center justify-center rounded-lg border-2 border-dashed px-4 ${
          compact ? "py-3" : "py-6"
        } ${
          isDragging
            ? "border-primary-400 bg-primary-50"
            : "border-neutral-300 bg-neutral-50 hover:bg-neutral-100"
        }`}
      >
        <div className="flex flex-col items-center gap-1 text-center">
          <i
            className="fa-duotone fa-thin fa-cloud-arrow-up text-2xl text-neutral-500"
            aria-hidden="true"
          />
          <span className="text-sm text-neutral-700">
            Drop files here or click to upload
          </span>
          {!compact && (
            <span className="text-xs text-neutral-500">
              Up to {MAX_FILES} files, 10 MB each
            </span>
          )}
        </div>
        <input
          ref={inputRef}
          type="file"
          multiple
          aria-label="Choose files to attach"
          className="hidden"
          onChange={handleInputChange}
          onClick={(e) => e.stopPropagation()}
        />
      </div>

      {error && <p className="text-xs text-red-600">{error}</p>}

      {value.length > 0 && (
        <ul className="space-y-1">
          {value.map((f, idx) => (
            <li
              key={`${f.name}-${idx}`}
              className="flex items-center justify-between rounded border border-neutral-200 bg-white px-3 py-1.5 text-sm"
            >
              <span className="flex items-center gap-2 truncate text-neutral-800">
                <i
                  className="fa-duotone fa-thin fa-paperclip text-neutral-500"
                  aria-hidden="true"
                />
                <span className="truncate">{f.name}</span>
                <span className="text-xs text-neutral-500">
                  ({formatBytes(f.size)})
                </span>
              </span>
              <button
                type="button"
                onClick={() => removeAt(idx)}
                className="text-xs text-neutral-500 hover:text-red-600"
                aria-label={`Remove ${f.name}`}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default AttachmentDropzone;
